import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useQuery } from '@tanstack/react-query';
import { getWebhookMutationKey } from '.';

import { webhookService } from '../../application/WebhookService';
import { WebhookActionType } from '../../context/webhook/actions';
import { useWebhookContext } from '../../context/webhook/context';
import { Webhook } from '../../domain/webhook/Webhook';
import useErrorMiddleware from '../errorMiddleware/useErrorMiddleware';
import { useShowSnackbarMessage } from '../snackbarMessage/useShowSnackbarMessage';
import { useRetrieveUserCredentials } from '../user/useRetrieveUserCredentials';

type UseRetrieveWebhooksProps = ServiceResponse<() => Promise<void>, Webhook[]>;

// Hooks act as 'Adapter' layer.
export const useRetrieveWebhooks = (projectId?: string): UseRetrieveWebhooksProps => {
  const { showSnackbarMessage } = useShowSnackbarMessage();
  const { data: userCredentials } = useRetrieveUserCredentials();
  const { webhookDispatch, webhookState } = useWebhookContext();
  const { sessionExpiredLogout } = useErrorMiddleware();

  const {
    isInitialLoading: isLoading,
    refetch,
    isError,
  } = useQuery(getWebhookMutationKey(projectId), () => webhookService.retrieveWebhooks(projectId!), {
    enabled: !!projectId && userCredentials?.subscription.test(`planner.webhooks`),
    retry: false,
    refetchOnWindowFocus: false,
    onSuccess: (webhooks) => {
      webhookDispatch({
        type: WebhookActionType.FETCH_WEBHOOKS_SUCCESS,
        payload: { webhooks },
      });
    },
    onError: (err: any) => {
      console.info(`Planner retrieve Webhooks Error: `, err?.response);

      if (err?.response?.status === 401) {
        sessionExpiredLogout();
        return;
      }

      showSnackbarMessage({
        messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
      });
    },
  });

  return {
    error: isError,
    success: true,
    isLoading,
    data: webhookState.webhooks,
    run: async () => {
      await refetch();
    },
  };
};
